import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as onboarding from "onboarding";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import BookCollectionStore from "stores/BookCollectionStore";
import type { BookData } from "stores/BookStore";
import stores from "stores/stores";
import Button from "ui/Button";
import { PublicLibraryList } from "views/PublicLibraryListView";
import SkeletonView from "views/SkeletonView";
import * as walkthrough from "walkthrough";

/** The language's starter stories, easiest first, or else its hottest easy ones */
const fetchFirstBooks = async (): Promise<BookData[]> => {
  const language = stores.user.currentLearningLanguage.value;
  const starterStories = languageMap.map[language].starterStories;
  if (starterStories) {
    const bookIDs = [starterStories.easy, starterStories.medium];
    const { data } = await readlangClient.request<BookData[]>(
      "GET",
      "/api/books",
      // Public, as signed-out visitors may only ask for those
      { bookIDs: bookIDs.join("|"), public: true }
    );
    return [...data].sort(
      (first, second) =>
        bookIDs.indexOf(first._id) - bookIDs.indexOf(second._id)
    );
  }
  const { data } = await readlangClient.request<BookData[]>(
    "GET",
    "/api/books",
    {
      public: true,
      language,
      firstLanguage: stores.user.firstLanguage.value,
      sortBy: "hotness",
      // Short stories (in characters for some languages, which suits too)
      filter: { metrics_CEF: "A1-A2", metrics_words: "100-1000" },
      limit: 2,
    }
  );
  return data;
};

interface State {
  loading: boolean;
}

/**
 * The top of Discover for a new account in the onboardingLibrary1
 * experiment's arm, until its first translation: two easy texts to start
 * with, then the means to read something of its own. (In the iOS app,
 * Discover's websites follow, just below; the website's Discover only links
 * to them when there are no texts to show.)
 */
export default class StartHereView extends preact.Component<{}, State> {
  state: State = { loading: true };
  private readonly books = new BookCollectionStore();
  private readonly shown =
    !walkthrough.hasAchievement("translate") && onboarding.libraryOnboarding();

  componentDidMount() {
    // Gone once they've translated
    walkthrough.events.on("new", this.update);
    if (this.shown) {
      void this.fetchBooks();
    }
  }

  componentWillUnmount() {
    walkthrough.events.off("new", this.update);
  }

  update = () => this.forceUpdate();

  async fetchBooks() {
    try {
      this.books.setBooks(await fetchFirstBooks());
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    if (!this.shown || walkthrough.hasAchievement("translate")) {
      return null;
    }
    return (
      <section class="startHere">
        <h2>Start here</h2>
        <p>
          Pick a story, and {deviceDetect.isTouchDevice() ? "tap" : "click"} any
          word you don't know to translate it.
        </p>
        {this.state.loading ? (
          <SkeletonView kind="cards" count={2} />
        ) : (
          <PublicLibraryList books={this.books} />
        )}
        <div class="startHereOwn">
          <p>Or read something of your own:</p>
          <Button href="/upload" icon="icon-circle-arrow-up">
            Upload a text
          </Button>
        </div>
      </section>
    );
  }
}
